/**
 * AprovacoesPanel.jsx — Rê Leve
 * Aba "Aprovações" da Janela do Operador.
 * Fila de perguntas que a Léve passou para a equipe.
 * Equipe edita a sugestão, aprova → vira aprendizado + RAG opcional.
 */

import { useEffect, useState } from 'react';
import {
  getPendingQuestions,
  answerQuestion,
  dismissQuestion,
  updateSuggestion,
  INTERVENTION_META,
} from '../lib/pendingQuestionsEngine';
import { subscribeAll } from '../lib/liveSyncEngine';

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function AprovacoesPanel() {
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all'); // all | question | human_request | sales_opportunity
  const allItems = getPendingQuestions({ status: filter });
  const items = typeFilter === 'all' ? allItems : allItems.filter(q => (q.type || 'question') === typeFilter);

  const refresh = () => setTick(t => t + 1);

  useEffect(() => {
    const unsub = subscribeAll(() => setTick(t => t + 1));
    return () => unsub();
  }, []);

  // Contadores por tipo (filtro de status aplicado)
  const counts = {
    all: allItems.length,
    question: allItems.filter(q => (q.type || 'question') === 'question').length,
    human_request: allItems.filter(q => q.type === 'human_request').length,
    sales_opportunity: allItems.filter(q => q.type === 'sales_opportunity').length,
  };

  return (
    <div className="rl-ops-panel">
      <div className="rl-ops-header">
        <div>
          <div className="rl-ops-eyebrow">Aprovações · toda intervenção humana cai aqui</div>
          <h1 className="rl-ops-title">A equipe entra em cena</h1>
          <p className="rl-ops-sub">
            Três tipos de intervenção que a Léve encaminha pra equipe:
            <strong> ❓ Pergunta sem resposta</strong> (vira aprendizado) ·
            <strong> 🙋 Cliente pediu humano</strong> (precisa atender) ·
            <strong> 💰 Oportunidade de venda</strong> (corporativo, alto-valor, pode fechar agora).
          </p>
        </div>
        <div className="rl-ops-actions">
          <div className="rl-filter-group">
            <button className={'rl-chip ' + (filter === 'pending' ? 'rl-chip--active' : '')}
              onClick={() => setFilter('pending')}>Pendentes</button>
            <button className={'rl-chip ' + (filter === 'answered' ? 'rl-chip--active' : '')}
              onClick={() => setFilter('answered')}>Respondidas</button>
            <button className={'rl-chip ' + (filter === 'all' ? 'rl-chip--active' : '')}
              onClick={() => setFilter('all')}>Todas</button>
          </div>
          <button className="rl-ops-refresh" onClick={refresh}>↻</button>
        </div>
      </div>

      {/* Filtro por tipo de intervenção */}
      <div className="rl-filter-group" style={{ marginBottom: 6 }}>
        <button className={'rl-chip ' + (typeFilter === 'all' ? 'rl-chip--active' : '')}
          onClick={() => setTypeFilter('all')}>Todos os tipos ({counts.all})</button>
        <button className={'rl-chip ' + (typeFilter === 'question' ? 'rl-chip--active' : '')}
          onClick={() => setTypeFilter('question')}>❓ Pergunta ({counts.question})</button>
        <button className={'rl-chip ' + (typeFilter === 'human_request' ? 'rl-chip--active' : '')}
          onClick={() => setTypeFilter('human_request')}>🙋 Quer humano ({counts.human_request})</button>
        <button className={'rl-chip ' + (typeFilter === 'sales_opportunity' ? 'rl-chip--active' : '')}
          onClick={() => setTypeFilter('sales_opportunity')}>💰 Oportunidade ({counts.sales_opportunity})</button>
      </div>

      <div className="rl-kpi-grid">
        <div className="rl-kpi">
          <div className="rl-kpi-label">Pendentes agora</div>
          <div className="rl-kpi-value">{getPendingQuestions({ status: 'pending' }).length}</div>
          <div className="rl-kpi-sub">aguardando equipe</div>
        </div>
        <div className="rl-kpi">
          <div className="rl-kpi-label">Respondidas (30d)</div>
          <div className="rl-kpi-value">{getPendingQuestions({ status: 'answered' }).length}</div>
          <div className="rl-kpi-sub">viraram aprendizado</div>
        </div>
        <div className="rl-kpi">
          <div className="rl-kpi-label">Tempo médio resposta</div>
          <div className="rl-kpi-value">~3h</div>
          <div className="rl-kpi-sub">meta: &lt; 2h</div>
        </div>
      </div>

      <div className="rl-list">
        {items.length === 0 && (
          <div className="rl-empty rl-empty--big">
            <div style={{ fontSize: 38, marginBottom: 8 }}>🌱</div>
            {filter === 'pending'
              ? 'Tudo certo por aqui — a Léve tá dando conta sozinha.'
              : 'Nada nessa categoria ainda.'}
          </div>
        )}
        {items.map(q => (
          <QuestionCard key={q.id} q={q} onChange={refresh} />
        ))}
      </div>
    </div>
  );
}

function QuestionCard({ q, onChange }) {
  const [editAnswer, setEditAnswer] = useState(q.suggestedAnswer || '');
  const [tags, setTags] = useState((q.suggestedTags || []).join(', '));
  const [saveToRAG, setSaveToRAG] = useState(true);
  const [busy, setBusy] = useState(false);

  function approve() {
    setBusy(true);
    answerQuestion(q.id, {
      answer: editAnswer.trim(),
      tags: tags.split(',').map(s => s.trim()).filter(Boolean),
      saveToRAG,
    });
    setBusy(false);
    onChange();
  }

  function dismiss() {
    const reason = prompt('Motivo de descartar (opcional):') || '';
    dismissQuestion(q.id, { reason });
    onChange();
  }

  if (q.status === 'answered') {
    return (
      <div className="rl-pending rl-pending--done">
        <div className="rl-pending-head">
          <div>
            <div className="rl-pending-customer">{q.customerName} <span className="rl-tag">{q.customerSegment}</span></div>
            <div className="rl-pending-meta">Respondida {timeAgo(q.answeredAt)} atrás</div>
          </div>
          <span className="rl-alert-tag rl-alert-tag--baixa">respondida</span>
        </div>
        <div className="rl-pending-question">"{q.question}"</div>
        <div className="rl-pending-answer">→ {q.finalAnswer}</div>
        {q.tags && q.tags.length > 0 && (
          <div className="rl-rag-tags">{q.tags.map(t => <span key={t} className="rl-tag">#{t}</span>)}</div>
        )}
      </div>
    );
  }

  if (q.status === 'dismissed') {
    return (
      <div className="rl-pending rl-pending--dismissed">
        <div className="rl-pending-head">
          <div>
            <div className="rl-pending-customer">{q.customerName}</div>
            <div className="rl-pending-meta">Descartada{q.dismissReason ? ' · ' + q.dismissReason : ''}</div>
          </div>
          <span className="rl-alert-tag rl-alert-tag--baixa">descartada</span>
        </div>
        <div className="rl-pending-question" style={{ opacity: 0.55 }}>"{q.question}"</div>
      </div>
    );
  }

  const meta = INTERVENTION_META[q.type || 'question'];

  return (
    <div className={'rl-pending rl-pending--type-' + (q.type || 'question')}>
      <div className="rl-pending-head">
        <div>
          <div className="rl-pending-customer">
            <span className="rl-leve-avatar" style={{ width: 30, height: 30, fontSize: 14 }}>{meta.icon}</span>
            <strong>{q.customerName}</strong>
            <span className="rl-tag">{q.customerSegment}</span>
            <span className={'rl-intervention-tag rl-intervention-tag--' + meta.color}>{meta.label}</span>
          </div>
          <div className="rl-pending-meta">há {timeAgo(q.askedAt)} · {q.context}</div>
          {q.estimatedValue && (
            <div style={{ fontSize: 12, color: 'var(--rl-verde)', fontWeight: 700, marginTop: 4 }}>
              💰 Potencial estimado: R$ {q.estimatedValue.toLocaleString('pt-BR')}/mês
            </div>
          )}
        </div>
        <span className="rl-alert-tag rl-alert-tag--alta">pendente</span>
      </div>

      <div className="rl-pending-question">"{q.question}"</div>
      <div className="rl-pending-bot">
        <span className="rl-leve-avatar" style={{ width: 24, height: 24, fontSize: 12 }}>🌱</span>
        <em>Léve respondeu na hora:</em> {q.leveResponse}
      </div>

      <div className="rl-edit-form" style={{ marginTop: 12 }}>
        <label className="rl-form-label">Resposta sugerida (edite antes de aprovar)</label>
        <textarea className="rl-input rl-input--text" rows={3} value={editAnswer}
          onChange={e => setEditAnswer(e.target.value)} />

        <label className="rl-form-label">Tags (vão pro RAG)</label>
        <input className="rl-input" value={tags}
          onChange={e => setTags(e.target.value)} placeholder="ex: cardápio, entrega, restrição" />

        <label className="rl-form-checkbox">
          <input type="checkbox" checked={saveToRAG} onChange={e => setSaveToRAG(e.target.checked)} />
          Salvar também no RAG (próxima vez a Léve responde direto)
        </label>

        <div className="rl-form-actions">
          <button className="rl-btn" onClick={approve} disabled={busy || !editAnswer.trim()}>
            ✓ Aprovar e enviar
          </button>
          <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={dismiss}>
            Descartar
          </button>
        </div>
      </div>
    </div>
  );
}
